import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { proposalApi } from '@/api/commercial/proposals';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/components/ui/form-error';
import { useFormError } from '@/hooks/use-form-error';
import { toast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-message';

interface ResumeReopenDialogProps {
  open: boolean;
  onClose: () => void;
  proposalId?: string;
  onSuccess?: () => void;
}

export function ResumeReopenDialog({
  open,
  onClose,
  proposalId,
  onSuccess,
}: ResumeReopenDialogProps) {
  const queryClient = useQueryClient();
  const { formError, showFormError, clearFormError } = useFormError(open);

  const reopenMutation = useMutation({
    mutationFn: () => {
      if (!proposalId) throw new Error('Proposal ID is required');
      return proposalApi.reopen(proposalId);
    },
    onSuccess: () => {
      clearFormError();
      toast({
        variant: 'success',
        title: 'Proposta reaberta',
        description: 'A proposta foi reaberta para edição.',
      });

      if (proposalId) {
        queryClient.invalidateQueries({ queryKey: ['proposal', proposalId] });
        queryClient.invalidateQueries({ queryKey: ['proposals'] });
      }

      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      showFormError(error);
      toast({
        title: 'Erro ao reabrir proposta',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Proposta</DialogTitle>
          <DialogDescription>Reabrir a proposta para edição.</DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <p className="text-sm text-amber-800">
            Ao editar, a proposta voltará para o status &quot;Aberta&quot; e precisará ser enviada
            novamente após as alterações.
          </p>
        </div>

        <FormError error={formError} onDismiss={clearFormError} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={reopenMutation.isPending}>
            Cancelar
          </Button>
          <Button
            className="bg-black text-white hover:bg-gray-800"
            onClick={() => reopenMutation.mutate()}
            disabled={reopenMutation.isPending}
          >
            {reopenMutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {reopenMutation.isPending ? 'Reabrindo...' : 'Confirmar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
